import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  FormsModule,
} from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ListboxModule } from 'primeng/listbox';
import { CheckboxModule } from 'primeng/checkbox';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { RolesService } from '../roles.service';
import { Role } from '../role.model';
import { TextareaModule } from 'primeng/textarea';
import { CardModule } from 'primeng/card';
import { Permission } from '../../permissions/permission.model';
import { PermissionService } from '../../permissions/permission.service';
import { Page } from '../../../models/page-wrapper.model';
import { EMPTY } from 'rxjs';
import { expand, reduce } from 'rxjs/operators';

@Component({
  selector: 'app-form-role',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    ToastModule,
    TextareaModule,
    CardModule,
    ListboxModule,
    CheckboxModule,
    FormsModule,
  ],
  templateUrl: './form-role.component.html',
  styleUrl: './form-role.component.css',
  providers: [MessageService],
})
export class FormRoleComponent implements OnInit {
  roleId: number | null = null;
  roleForm!: FormGroup;
  isEditMode = false;
  pageTitle = 'Create New Role';
  permissions: Permission[] = [];
  groupedPermissions: { resourceName: string; resourceId: number; permissions: Permission[] }[] = [];
  selectedPermissions: number[] = [];
  permissionsLoaded = false;
  permissionsLoadFailed = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private rolesService: RolesService,
    private messageService: MessageService,
    private permissionService: PermissionService
  ) {}

  ngOnInit(): void {
    this.getAllPermissions();
    this.initForm();
    this.route.paramMap.subscribe((params) => {
      const id = params.get('id');
      if (id) {
        this.roleId = +id;
        this.isEditMode = true;
        this.pageTitle = 'Edit Role';
        this.loadRoleData(this.roleId);
      }
    });
  }

  getAllPermissions(): void {
    this.permissionsLoaded = false;
    this.permissionsLoadFailed = false;
    this.permissionService
      .getAllPermissions({
        page: 0,
        size: 200,
      })
      .pipe(
        expand((page: Page<Permission>) =>
          page.last
            ? EMPTY
            : this.permissionService.getAllPermissions({ page: page.number + 1, size: 200 })
        ),
        reduce((permissions: Permission[], page: Page<Permission>) =>
          permissions.concat(page.content), [] as Permission[])
      )
      .subscribe({
        next: (permissions) => {
          this.permissions = permissions;
          this.groupedPermissions = this.groupPermissionsByResource(permissions);
          this.permissionsLoaded = true;
        },
        error: () => {
          this.permissionsLoadFailed = true;
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load permissions.' });
        },
      });
  }

  // group permissions in the same resource
  groupPermissionsByResource(permissions: Permission[]) {
    const grouped: Record<
      string,
      { resourceName: string; resourceId: number; permissions: Permission[] }
    > = {};

    permissions.forEach((permission) => {
      const resourceName = permission.resource?.resourceName;
      if (!grouped[resourceName]) {
        grouped[resourceName] = {
          resourceName,
          resourceId: permission.resource?.id,
          permissions: [],
        };
      }
      grouped[resourceName].permissions.push(permission);
    });
    return Object.values(grouped).sort((a, b) => a.resourceName.localeCompare(b.resourceName));
  }

  initForm(): void {
    this.roleForm = this.fb.group({
      roleName: ['', Validators.required],
      description: [''],
      permissions: [[]],
    });
  }

  loadRoleData(id: number): void {
    this.rolesService.getRoleById(id).subscribe({
      next: (role) => {
        this.selectedPermissions =
          role.permissions?.map((p: any) => p.id) ?? [];
        this.roleForm.patchValue({
          roleName: role.roleName,
          description: role.description,
          permissions: this.selectedPermissions,
        });
      },
    });
  }

  isPermissionSelected(id: number): boolean {
    return this.roleForm.get('permissions')?.value?.includes(id);
  }

  togglePermission(id: number): void {
    const control = this.roleForm.get('permissions');
    const selected = control?.value || [];

    if (selected.includes(id)) {
      control?.setValue(selected.filter((permId: number) => permId !== id));
    } else {
      control?.setValue([...selected, id]);
    }
  }

  saveRole(): void {
    if (!this.permissionsLoaded) {
      return;
    }
    if (this.roleForm.invalid) {
      this.roleForm.markAllAsTouched();
      return;
    }
     const permissionsWithResource = this.roleForm.value.permissions.map(
      (permId: number) => {
        const perm = this.permissions.find((p) => p.id === permId);
        return {
          id: permId,
          action: perm?.action,
          description: perm?.description,
          resource: {
            id: perm?.resource?.id ?? null, // لو مش لاقي resource رجع null أو غيره
          },
        };
      }
    );

    const roleData: Role = {
      id: this.roleId ?? 0,
      ...this.roleForm.value,
      permissions: permissionsWithResource,
    };

    const operation = this.isEditMode
      ? this.rolesService.updateRole(roleData)
      : this.rolesService.createRole(roleData);

    operation.subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `Role ${
            this.isEditMode ? 'updated' : 'created'
          } successfully.`,
        });
        setTimeout(
          () =>
            this.router.navigate([
              '/ai-studio/session/current',
              { outlets: { canvasOutlet: ['roles', 'list'] } },
            ]),
          1500
        );
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: `Failed to ${this.isEditMode ? 'update' : 'create'} role.`,
        });
        console.error(err);
      },
    });
  }

  cancel(): void {
    this.router.navigate([
      '/ai-studio/session/current',
      { outlets: { canvasOutlet: ['roles', 'list'] } },
    ]);
  }
}
